<div class="coi-view-container">

  @if (loading) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading COI...</p>
    </div>
  }

  @if (error) {
    <mat-card class="error-card">
      <mat-card-content>
        <mat-icon color="warn">error</mat-icon>
        <h2>{{ error }}</h2>
        <button mat-raised-button color="primary" (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Return to COIs
        </button>
      </mat-card-content>
    </mat-card>
  }

  @if (!loading && !error && currentCoi) {

    <mat-sidenav-container class="sidenav-container">

      <!-- Actions Sidenav (Left Side) -->
      <mat-sidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          title="COI Actions"
          [actions]="sidenavActions"
          [tabs]="sidenavTabs"
          [selectedTabIndex]="selectedTabIndex"
          (tabSelected)="selectTab($event)">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <!-- Main Content -->
      <mat-sidenav-content class="form-content" [class.with-viewer]="showDocumentViewer">

        <!-- Archived Version Banner -->
        @if (currentCoi.activeContract === false) {
          <div class="archived-banner">
            <mat-icon>archive</mat-icon>
            <span>Archived Version — Superseded on {{ currentCoi.updatedAt | date:'MM/dd/yyyy' }}</span>
          </div>
        }

        <!-- Header Actions Bar -->
        <div class="header-bar">
          <button mat-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            Back to COIs
          </button>

          <div class="header-actions">
            @if (currentDocument) {
              <button mat-stroked-button (click)="toggleDocumentViewer()">
                <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                {{ showDocumentViewer ? 'Hide' : 'View' }} COI
              </button>
              <button mat-stroked-button (click)="downloadDocument()">
                <mat-icon>download</mat-icon>
                Download
              </button>
            }
            <button mat-raised-button color="primary" (click)="editCoi()">
              <mat-icon>edit</mat-icon>
              Edit COI
            </button>
          </div>
        </div>

        <!-- Static Information Section (Always Visible) -->
        <div class="static-info-section">
          <!-- COI Name Header -->
          <div class="coi-name-header">
            <h1>Certificate of Insurance</h1>
            <div class="coi-metadata">
              <span>COI ID: {{ currentCoi.coiId || 'N/A' }}</span>
              @if (currentCoi.coiNum) {
                <span class="separator">•</span>
                <span>COI Number: {{ currentCoi.coiNum }}</span>
              }
            </div>
            @if (currentDocument) {
              <div class="document-preview">
                <mat-icon color="primary">description</mat-icon>
                <span class="main-document-label">COI Document</span>
                <span class="file-type-badge">{{ currentDocument.fileType.toUpperCase() }}</span>
                <button mat-icon-button
                        [color]="showDocumentViewer ? 'primary' : ''"
                        (click)="toggleDocumentViewer()"
                        matTooltip="Toggle COI viewer">
                  <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <div class="version-info">
                  <span class="version-label">Version</span>
                  @if (!currentCoi.versionList || currentCoi.versionList.length === 0) {
                    <span class="version-value">1 of 1</span>
                  } @else {
                    <span class="version-value">{{ currentCoi.version }} of {{ currentCoi.versionList.length }}</span>
                  }
                </div>
                <button mat-stroked-button
                        (click)="selectTab(4)"
                        class="compact-button">
                  COI History
                </button>
              </div>
            }
          </div>

          <!-- Essential Information -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>business</mat-icon>
              <h3>Essential Information</h3>
            </div>
            <div class="info-grid essential-grid">
              <div class="info-field">
                <label>Main Document Name</label>
                <div class="value">{{ currentCoi.mainDocumentName || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Status</label>
                <div class="value">
                  <mat-chip-set>
                    <mat-chip [highlighted]="currentCoi.status === 'Active'">
                      {{ currentCoi.status || 'N/A' }}
                    </mat-chip>
                  </mat-chip-set>
                </div>
              </div>

              <div class="info-field">
                <label>Party Providing COI</label>
                <div class="value">{{ partyProvidingCoiName }}</div>
              </div>

              <div class="info-field">
                <label>Insured Party</label>
                <div class="value">{{ insuredPartyName }}</div>
              </div>

              <div class="info-field">
                <label>Insurance Provider</label>
                <div class="value">{{ insuranceProviderName }}</div>
              </div>

              <div class="info-field">
                <label>Legal Entities</label>
                <div class="value">{{ legalEntitiesDisplay || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Primary Owner</label>
                <div class="value">{{ primaryOwnerName }}</div>
              </div>

              <div class="info-field">
                <label>Coverage Types</label>
                <div class="value">{{ (currentCoi.coverageTypes && currentCoi.coverageTypes.length > 0) ? currentCoi.coverageTypes.join(', ') : 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Departments</label>
                <div class="value">{{ (currentCoi.departments && currentCoi.departments.length > 0) ? currentCoi.departments.join(', ') : 'N/A' }}</div>
              </div>

            </div>
          </div>

          <!-- Timeline & Terms -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>schedule</mat-icon>
              <h3>Timeline & Dates</h3>
            </div>
            <div class="info-grid terms-grid">
              <div class="info-field">
                <label>Effective Date</label>
                <div class="value">{{ formatDate(currentCoi.effectiveDate) }}</div>
              </div>

              <div class="info-field">
                <label>Expiration Date</label>
                <div class="value">{{ formatDate(currentCoi.expirationDate) }}</div>
              </div>

              <div class="info-field">
                <label>Last Review Date</label>
                <div class="value">{{ formatDate(currentCoi.lastReviewDate) }}</div>
              </div>

              <div class="info-field">
                <label>Next Review Date</label>
                <div class="value">{{ formatDate(currentCoi.nextReviewDate) }}</div>
              </div>

              <div class="info-field">
                <label>Review Period</label>
                <div class="value">{{ currentCoi.reviewPeriodInDays ? currentCoi.reviewPeriodInDays + ' days' : 'N/A' }}</div>
              </div>
            </div>
          </div>

          <!-- Coordinators & Signatories -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>person</mat-icon>
              <h3>Coordinators & Signatories</h3>
            </div>
            <div class="info-grid">
              <div class="info-field">
                <label>Carrier Signatory</label>
                <div class="value">{{ currentCoi.carrierSignatory || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Internal Signatory</label>
                <div class="value">{{ internalSignatoryName }}</div>
              </div>

              <div class="info-field">
                <label>Internal Coordinator</label>
                <div class="value">{{ internalCoordinatorName }}</div>
              </div>

              <div class="info-field">
                <label>External Coordinator</label>
                <div class="value">{{ currentCoi.externalCoordinator || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Secondary Owner</label>
                <div class="value">{{ secondaryOwnerName }}</div>
              </div>

              <div class="info-field">
                <label>Tertiary Owner</label>
                <div class="value">{{ currentCoi.tertiaryOwner || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <!-- Additional Information -->
          @if (currentCoi.description) {
            <div class="info-section">
              <div class="section-header">
                <mat-icon>notes</mat-icon>
                <h3>Description</h3>
              </div>
              <div class="info-field full-width">
                <div class="value description-text">{{ currentCoi.description }}</div>
              </div>
            </div>
          }
        </div>

        <!-- Tabbed Content -->
        <mat-tab-group
          [selectedIndex]="selectedTabIndex"
          (selectedIndexChange)="selectTab($event)"
          class="coi-tabs">

          <!-- Tab 0: Associated Contracts -->
          <mat-tab label="Associated Contracts">
            <div class="tab-content">
              @if (currentCoi.contracts && currentCoi.contracts.length > 0) {
                <app-contract-connections
                  [selectedContractList]="currentCoi.contracts">
                </app-contract-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>link</mat-icon>
                    <p>No contracts associated with this COI</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- BAAs tab - Hidden, may restore later -->
          <!-- COIs tab - Hidden, may restore later -->

          <!-- Tab 2: Attachments -->
          <mat-tab label="Attachments">
            <div class="tab-content">
              @if (entityAttachments && entityAttachments.length > 0) {
                <app-attachment-connections
                  [attachmentObjectArray]="entityAttachments"
                  (attachmentUpdated)="refreshAttachments()">
                </app-attachment-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>attach_file</mat-icon>
                    <p>No attachments for this COI</p>
                    <button mat-raised-button color="primary" (click)="addAttachment()">
                      <mat-icon>add</mat-icon>
                      Add Attachment
                    </button>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 5: Important Dates -->
          <mat-tab label="Important Dates">
            <div class="tab-content">
              @if (entityDates && entityDates.length > 0) {
                <app-critical-date-connections
                  [dateObjectArray]="entityDates"
                  (criticalDateUpdated)="refreshCriticalDates()">
                </app-critical-date-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>event</mat-icon>
                    <p>No important dates for this COI</p>
                    <button mat-raised-button color="primary" (click)="addCriticalDate()">
                      <mat-icon>add</mat-icon>
                      Add Important Date
                    </button>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 6: People -->
          <mat-tab label="People">
            <div class="tab-content">
              <app-people-table
                [contractIds]="currentCoi.contracts || []">
              </app-people-table>
            </div>
          </mat-tab>

          <!-- Tab 7: COI History (Versions) -->
          <mat-tab label="COI History">
            <div class="tab-content">
              <app-versions
                entityType="coi"
                [versionList]="currentCoi.versionList || []"
                [currentEntityNum]="currentCoi.coiNum || ''">
              </app-versions>
            </div>
          </mat-tab>

          <!-- Tab 8: Provisions -->
          <mat-tab label="Provisions">
            <div class="tab-content">
              <mat-card class="empty-state">
                <mat-card-content>
                  <mat-icon>gavel</mat-icon>
                  <p>Provisions content coming soon</p>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>

          <!-- Tab 9: Notes -->
          <mat-tab label="Notes">
            <div class="tab-content">
              <app-notes
                [notes]="currentCoi?.notes || []"
                (notesSaved)="saveNotes($event)">
              </app-notes>
            </div>
          </mat-tab>

          <!-- HCP Benefits tab - Hidden, may restore later -->
          <!-- Data Tables tab - Hidden, may restore later -->

          <!-- Tab 8: Assessments -->
          <mat-tab label="Assessments">
            <div class="tab-content">
              <app-assessment-connections
                [entityId]="currentCoi.coiId || ''"
                entityType="COI"
                [sourceRecord]="currentCoi.friendlyName || currentCoi.insuranceProvider || ''">
              </app-assessment-connections>
            </div>
          </mat-tab>

          <!-- Workflow tab - Hidden, may restore later -->
          <!-- Enable Timesheets tab - Hidden, may restore later -->

        </mat-tab-group>

      </mat-sidenav-content>

      <!-- Document Viewer - Using Reusable Component (Right Side) -->
      <mat-sidenav mode="side"
                  position="end"
                  [opened]="showDocumentViewer"
                  class="coi-viewer">
        <app-document-viewer-sidenav
          [document]="currentDocument"
          [title]="'COI Viewer'"
          (close)="toggleDocumentViewer()"
          (download)="downloadDocument()"
          (openNewTab)="openDocumentInNewTab()">
        </app-document-viewer-sidenav>
      </mat-sidenav>

    </mat-sidenav-container>

  }

</div>
